/**
 * AdminLocations — super-admin management of the countries & regions that drive
 * the guest-estimate form's Country / Region selects.
 *
 * - Add a country (name, ISO-2, optional region label). Its subdivisions are
 *   populated automatically from a bundled, offline dataset and the region label
 *   is suggested from the ISO code.
 * - Per country: rename, set/adopt the region label, pin a display order (blank =
 *   alphabetical), toggle active, add/edit/override/remove/toggle regions,
 *   re-import (merges by stable code so overrides survive), delete (cascades).
 */

import React, { useEffect, useState } from 'react';
import {
  Globe, Plus, Trash2, Save, Loader2, ChevronDown, ChevronRight,
  DownloadCloud, AlertTriangle, CheckCircle2, Wand2,
} from 'lucide-react';
import {
  AdminCountry, AdminRegion,
  fetchAdminGeo, createCountry, updateCountry, deleteCountry, importCountryRegions,
} from '../../services/adminGeoApi';

interface Draft {
  name: string;
  regionLabel: string;
  /** Explicit display order, or null = unset (alphabetical on the form). */
  sortOrder: number | null;
  isActive: boolean;
  regions: AdminRegion[];
}

const blankRegion = (): AdminRegion => ({ name: '', code: '', sortOrder: 0, isActive: true });

export const AdminLocations: React.FC = () => {
  const [countries, setCountries] = useState<AdminCountry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [importingId, setImportingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  // New-country form
  const [newName, setNewName] = useState('');
  const [newIso, setNewIso] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      setCountries(await fetchAdminGeo());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(null), 4000); };

  const expand = (c: AdminCountry) => {
    if (expandedId === c.id) { setExpandedId(null); setDraft(null); return; }
    setExpandedId(c.id);
    setDraft({
      name: c.name,
      regionLabel: c.regionLabel,
      sortOrder: c.sortOrder,
      isActive: c.isActive,
      regions: c.regions.map(r => ({ ...r })),
    });
  };

  const patchDraft = (patch: Partial<Draft>) => setDraft(prev => prev ? { ...prev, ...patch } : prev);
  const patchRegion = (i: number, patch: Partial<AdminRegion>) =>
    setDraft(prev => prev ? { ...prev, regions: prev.regions.map((r, j) => j === i ? { ...r, ...patch } : r) } : prev);
  const removeRegion = (i: number) =>
    setDraft(prev => prev ? { ...prev, regions: prev.regions.filter((_, j) => j !== i) } : prev);
  const addRegion = () =>
    setDraft(prev => prev ? { ...prev, regions: [...prev.regions, blankRegion()] } : prev);

  const saveCountry = async (id: number) => {
    if (!draft) return;
    setSavingId(id);
    try {
      await updateCountry(id, {
        name: draft.name.trim(),
        regionLabel: draft.regionLabel.trim() || 'Region',
        sortOrder: draft.sortOrder,
        isActive: draft.isActive,
        regions: draft.regions
          .filter(r => r.name.trim() !== '')
          .map((r, idx) => ({ ...r, name: r.name.trim(), code: (r.code || '').trim() || null, sortOrder: idx * 10 })),
      });
      flash('Saved.');
      setExpandedId(null);
      setDraft(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  // Toggle active inline from the row (no need to expand).
  const toggleActive = async (c: AdminCountry) => {
    try {
      await updateCountry(c.id, { isActive: !c.isActive });
      setCountries(prev => prev.map(x => x.id === c.id ? { ...x, isActive: !x.isActive } : x));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  };

  const doImport = async (id: number) => {
    setImportingId(id);
    try {
      const res = await importCountryRegions(id);
      if (res.imported) {
        flash(`Added ${res.imported} region(s) from the bundled list.`);
        // If this country is open, refresh the draft with the new regions.
        if (expandedId === id) {
          setDraft({
            name: res.country.name,
            regionLabel: res.country.regionLabel,
            sortOrder: res.country.sortOrder,
            isActive: res.country.isActive,
            regions: res.country.regions.map(r => ({ ...r })),
          });
        }
        await reload();
      } else {
        flash(res.sourceCount === 0
          ? 'No subdivisions are bundled for this country — add them by hand.'
          : 'Already complete — every bundled region is present.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImportingId(null);
    }
  };

  const create = async () => {
    if (!newName.trim()) { setError('Country name is required'); return; }
    if (!/^[A-Za-z]{2}$/.test(newIso.trim())) { setError('ISO code must be 2 letters (e.g. CA, US, KY)'); return; }
    setCreating(true);
    setError(null);
    try {
      const res = await createCountry({
        name: newName.trim(),
        isoCode: newIso.trim().toUpperCase(),
        regionLabel: newLabel.trim() || undefined,
      });
      const label = res.country.regionLabel;
      flash(res.imported
        ? `Added ${res.country.name} with ${res.imported} ${label.toLowerCase()}(s).`
        : `Added ${res.country.name}. No subdivisions are bundled for it — add them by hand.`);
      setNewName(''); setNewIso(''); setNewLabel('');
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await deleteCountry(id);
      setConfirmDeleteId(null);
      if (expandedId === id) { setExpandedId(null); setDraft(null); }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  return (
    <div className="container-fluid py-3" style={{ maxWidth: 980 }}>
      <h4 className="fw-bold d-flex align-items-center gap-2 mb-1">
        <Globe size={20} /> Locations — Countries &amp; Regions
      </h4>
      <p className="text-muted small mb-3">
        Drives the Country / Region selects on the guest-estimate form. Names are
        submitted to Pardot/Salesforce verbatim — keep them proper-cased.
      </p>

      {error && (
        <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}
      {notice && (
        <div className="alert alert-success py-2 px-3 small d-flex align-items-center gap-2">
          <CheckCircle2 size={14} /> {notice}
        </div>
      )}

      {/* Add country */}
      <div className="card mb-4">
        <div className="card-body">
          <h6 className="fw-bold mb-3">Add a country</h6>
          <div className="row g-2 align-items-end">
            <div className="col-sm-4">
              <label className="form-label small fw-semibold mb-1">Country name</label>
              <input className="form-control form-control-sm" value={newName}
                onChange={e => setNewName(e.target.value)} placeholder="e.g. Mexico" maxLength={100} />
            </div>
            <div className="col-sm-2">
              <label className="form-label small fw-semibold mb-1">ISO (2)</label>
              <input className="form-control form-control-sm font-monospace text-uppercase" value={newIso}
                onChange={e => setNewIso(e.target.value)} placeholder="MX" maxLength={2} />
            </div>
            <div className="col-sm-3">
              <label className="form-label small fw-semibold mb-1">Region label</label>
              <input className="form-control form-control-sm" value={newLabel}
                onChange={e => setNewLabel(e.target.value)} placeholder="Auto — from ISO" maxLength={40} />
            </div>
            <div className="col-sm-3 d-flex align-items-end">
              <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1 w-100 justify-content-center"
                onClick={create} disabled={creating}>
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Country
              </button>
            </div>
          </div>
          <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.78rem' }}>
            Subdivisions are populated automatically from a bundled, offline list and
            the region label is derived from the ISO code. Leave the label blank to use
            the suggestion. The rare country with no bundled subdivisions can be filled
            in by hand after adding.
          </p>
        </div>
      </div>

      {/* Country list */}
      {loading ? (
        <div className="text-center py-5 text-muted"><Loader2 size={20} className="animate-spin" /> Loading…</div>
      ) : countries.length === 0 ? (
        <p className="text-muted">No countries yet. Add one above.</p>
      ) : (
        <div className="vstack gap-2">
          {countries.map(c => {
            const open = expandedId === c.id;
            return (
              <div key={c.id} className={`card ${c.isActive ? '' : 'opacity-75'}`}>
                <div className="card-body py-2">
                  <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-link text-decoration-none p-0 d-flex align-items-center gap-1 flex-grow-1 text-start"
                      onClick={() => expand(c)}>
                      {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <span className="fw-semibold">{c.name}</span>
                      <span className="badge text-bg-light font-monospace">{c.isoCode}</span>
                      <span className="text-muted small">· {c.regionLabel} · {c.regions.length} region(s)</span>
                      {c.sortOrder != null && <span className="badge text-bg-info" title="Pinned display order">#{c.sortOrder}</span>}
                      {!c.isActive && <span className="badge text-bg-secondary">disabled</span>}
                    </button>

                    <div className="form-check form-switch m-0" title={c.isActive ? 'Active' : 'Disabled'}>
                      <input type="checkbox" role="switch" className="form-check-input"
                        checked={c.isActive} onChange={() => toggleActive(c)} />
                    </div>

                    <button className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                      onClick={() => doImport(c.id)} disabled={importingId === c.id} title="Refresh regions from the bundled list">
                      {importingId === c.id ? <Loader2 size={13} className="animate-spin" /> : <DownloadCloud size={13} />}
                    </button>

                    {confirmDeleteId === c.id ? (
                      <span className="d-inline-flex align-items-center gap-1">
                        <button className="btn btn-sm btn-danger" onClick={() => remove(c.id)}>Delete</button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                      </span>
                    ) : (
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setConfirmDeleteId(c.id)} title="Delete country (and its regions)">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>

                  {open && draft && (
                    <div className="mt-3 pt-3 border-top">
                      <div className="row g-2 mb-2">
                        <div className="col-sm-5">
                          <label className="form-label small fw-semibold mb-1">Country name</label>
                          <input className="form-control form-control-sm" value={draft.name}
                            onChange={e => patchDraft({ name: e.target.value })} maxLength={100} />
                        </div>
                        <div className="col-sm-4">
                          <label className="form-label small fw-semibold mb-1">Region label</label>
                          <input className="form-control form-control-sm" value={draft.regionLabel}
                            onChange={e => patchDraft({ regionLabel: e.target.value })} maxLength={40}
                            placeholder="State / Province / District" />
                        </div>
                        <div className="col-sm-3">
                          <label className="form-label small fw-semibold mb-1" title="Empty = automatic (alphabetical after pinned countries)">
                            Display order
                          </label>
                          <input className="form-control form-control-sm" type="number" min={0}
                            value={draft.sortOrder ?? ''} placeholder="Auto"
                            onChange={e => patchDraft({ sortOrder: e.target.value === '' ? null : Number(e.target.value) })} />
                        </div>
                      </div>

                      {/* Label suggestion from the bundled data */}
                      {c.suggestedRegionLabel
                        && c.suggestedRegionLabel.toLowerCase() !== draft.regionLabel.trim().toLowerCase() && (
                        <div className="small text-muted mb-2 d-flex align-items-center gap-2">
                          <AlertTriangle size={13} className="text-warning" />
                          <span>Suggested label for {c.isoCode}: <strong>{c.suggestedRegionLabel}</strong></span>
                          <button type="button" className="btn btn-sm btn-outline-primary py-0 px-2 d-inline-flex align-items-center gap-1"
                            onClick={() => patchDraft({ regionLabel: c.suggestedRegionLabel as string })}>
                            <Wand2 size={11} /> Use
                          </button>
                        </div>
                      )}

                      <div className="form-check form-switch mb-3">
                        <input id={`act-${c.id}`} type="checkbox" role="switch" className="form-check-input"
                          checked={draft.isActive} onChange={e => patchDraft({ isActive: e.target.checked })} />
                        <label htmlFor={`act-${c.id}`} className="form-check-label small">Active (shown on the guest form)</label>
                      </div>

                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <span className="small fw-semibold">{draft.regionLabel || 'Region'}s</span>
                        <button className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1" onClick={addRegion}>
                          <Plus size={12} /> Add {draft.regionLabel || 'Region'}
                        </button>
                      </div>

                      {draft.regions.length === 0 ? (
                        <p className="text-muted small">No regions. Add some, or use the import button above.</p>
                      ) : (
                        <div className="vstack gap-1" style={{ maxHeight: 360, overflowY: 'auto' }}>
                          {draft.regions.map((r, i) => (
                            <div key={i} className="d-flex align-items-center gap-1">
                              <input className="form-control form-control-sm" style={{ flex: 3 }} value={r.name}
                                onChange={e => patchRegion(i, { name: e.target.value })} placeholder="Region name" maxLength={100} />
                              <input className="form-control form-control-sm font-monospace" style={{ flex: 1 }} value={r.code ?? ''}
                                onChange={e => patchRegion(i, { code: e.target.value })} placeholder="code" maxLength={12} />
                              <div className="form-check form-switch m-0" title={r.isActive ? 'Active' : 'Disabled'}>
                                <input type="checkbox" role="switch" className="form-check-input"
                                  checked={r.isActive} onChange={e => patchRegion(i, { isActive: e.target.checked })} />
                              </div>
                              <button className="btn btn-sm btn-outline-danger px-1" onClick={() => removeRegion(i)} title="Remove"><Trash2 size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="d-flex gap-2 mt-3">
                        <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                          onClick={() => saveCountry(c.id)} disabled={savingId === c.id}>
                          {savingId === c.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Changes
                        </button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => { setExpandedId(null); setDraft(null); }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminLocations;
